import { Component, Inject, OnInit, HostListener } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators, FormArray } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { FilterService } from '../api/services/filter.service';
import { MapService } from '../api/services/map.service';
import { TextInputComponent } from '../shared/forms/text-input/text-input.component';
import { TextAreaComponent } from '../shared/forms/text-area/text-area-input.component';
import { SlideToggleInputComponent } from '../shared/forms/slide-select/slide-toggle-input.component';
import { SelectInputComponent } from '../shared/forms/select-input/select-input.component';
import { MaporiumAccordionItemComponent } from '../shared/components/maporium-accordion/maporium-accordion-item.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  FilterDto,
  FilterSubject,
  FilterVerb,
  FilterMatchType,
  FilterConditionType,
  FilterActionType,
  FilterConditionDto,
  FilterActionDto
} from '@maporium-workspace/shared';

@Component({
  selector: 'maporium-create-filter-dialog',
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0">
        <span class="material-symbols-rounded me-2">{{data ? 'edit' : 'add_circle'}}</span>
        {{ (data ? 'FILTER_DIALOG.TITLE_UPDATE' : 'FILTER_DIALOG.TITLE_CREATE') | translate }}
      </h2>
      <span class="icon-smap-action-close close-button" mat-dialog-close [matTooltip]="'SHARE_DIALOG.CLOSE_TOOLTIP' | translate"></span>
    </div>

    <div mat-dialog-content>
      <form [formGroup]="myForm">
        <div class="mb-3">
          <app-text-input [label]="'CREATE_EDIT_SMAP.FIELDS.NAME' | translate" formControlName="name" [autoFocusAndSelect]="true"></app-text-input>
          <app-text-area [label]="'CREATE_EDIT_SMAP.FIELDS.DESCRIPTION' | translate" formControlName="description"></app-text-area>
        </div>

        <maporium-accordion-item [title]="'FILTER_DIALOG.DEFINITION' | translate" [sections]="conditionsArray?.length || 0">
          <div class="conditions-container">
            <div formArrayName="conditions" class="conditions-list" cdkDropList (cdkDropListDropped)="dropCondition($event)">
              <ng-container *ngFor="let condition of conditionsArray?.controls; let i = index">
                <div [formGroupName]="i"
                     class="condition-item mb-2 p-2 rounded d-flex align-items-center"
                     [class.selected-row]="i === selectedConditionIndex"
                     cdkDrag>
                  <!-- Drag Handle -->
                  <div class="drag-handle-container pe-2">
                    <span class="material-symbols-rounded drag-handle" 
                          cdkDragHandle
                          [matTooltip]="'FILTER_DIALOG.DRAG_TO_REORDER' | translate">drag_handle</span>
                  </div>

                  <!-- Condition Content -->
                  <div class="condition-content flex-grow-1" (click)="selectCondition(i)">
                    <div class="row">
                      <div class="col-4">
                        <app-select-input
                          formControlName="subject"
                          [dropdownColSize]="12"
                          [noMargin]="true"
                          [options]="subjectOptions"></app-select-input>
                      </div>
                      <div class="col-4">
                        <app-select-input
                          formControlName="verb"
                          [dropdownColSize]="12"
                          [noMargin]="true"
                          [options]="verbOptions"></app-select-input>
                      </div>
                      <div class="col-4">
                        <app-text-input
                          [noTopMargin]="true"
                          formControlName="object"></app-text-input>
                      </div>
                    </div>
                    <div class="row" *ngIf="condition?.get('subject')?.value === 'property'">
                      <div class="col-12">
                        <app-text-input
                          formControlName="propertyName"
                          [placeholder]="'FILTER_DIALOG.PROPERTY_NAME_PLACEHOLDER' | translate"></app-text-input>
                      </div>
                    </div>
                  </div>
                </div>
              </ng-container>
            </div>

            <!-- Add/Remove buttons positioned like metadata -->
            <div class="mb-2 condition-buttons d-flex justify-content-end">
              <span
                (click)="addCondition()"
                [matTooltip]="'FILTER_DIALOG.ADD_CONDITION' | translate"
                class="material-symbols-rounded me-2 add-button">
                add_circle
              </span>
              <span
                (click)="removeCondition(selectedConditionIndex!)"
                [class.disabled]="selectedConditionIndex === null || (conditionsArray?.length || 0) <= 1"
                [matTooltip]="'FILTER_DIALOG.REMOVE_CONDITION' | translate"
                class="material-symbols-rounded remove-button">
                do_not_disturb_on
              </span>
            </div>
          </div>

          <div formGroupName="action" class="action-form">
            <p class="mb-3">
              {{ 'FILTER_DIALOG.ELEMENTS_THAT' | translate }}
              <app-select-input
                class="d-inline-block mx-2 action-select"
                formControlName="matchType"
                [noMargin]="true"
                [dropdownColSize]="12"
                [options]="matchTypeOptions"></app-select-input>
              <app-select-input
                class="d-inline-block mx-2 action-select"
                formControlName="conditionType"
                [noMargin]="true"
                [dropdownColSize]="12"
                [options]="conditionTypeOptions"></app-select-input>
              <app-select-input
                class="d-inline-block mx-2 action-select"
                formControlName="actionType"
                [noMargin]="true"
                [dropdownColSize]="12"
                [options]="actionTypeOptions"></app-select-input>
            </p>
          </div>
        </maporium-accordion-item>

        <maporium-accordion-item [title]="'FILTER_DIALOG.CONFIGURATION' | translate" [sections]="2">
          <app-slide-toggle-input [label]="'visibility_off'"
                                  [isIcon]="true"
                                  [matTooltip]="'FILTER_DIALOG.HIDE_FROM_VIEWERS_TOOLTIP' | translate"
                                  [tooltip]="'FILTER_DIALOG.HIDE_FROM_VIEWERS' | translate"
                                  formControlName="excludeFromReadOnly"></app-slide-toggle-input>
          <app-slide-toggle-input [label]="'public'"
                                  [isIcon]="true"
                                  [matTooltip]="'FILTER_DIALOG.GLOBAL_FILTER_TOOLTIP' | translate"
                                  [tooltip]="'FILTER_DIALOG.GLOBAL_FILTER' | translate"
                                  formControlName="isGlobal"></app-slide-toggle-input>
        </maporium-accordion-item>
      </form>
    </div>

    <div mat-dialog-actions class="d-flex justify-content-evenly">
      <button class="dialog-action-btn dialog-action-btn-cancel" mat-dialog-close>{{ 'FILTER_DIALOG.ACTIONS.CANCEL' | translate }}</button>
      <button class="dialog-action-btn dialog-action-btn-primary"
              [disabled]="!myForm?.valid"
              (click)="data ? edit() : create()">{{ (data ? 'FILTER_DIALOG.ACTIONS.UPDATE' : 'FILTER_DIALOG.ACTIONS.CREATE') | translate }}</button>
    </div>
  `,
  styleUrls: ['./create-filter-dialog.component.scss'],
  imports: [
    MatDialogModule,
    MatInputModule,
    MatButtonModule,
    CommonModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatCheckboxModule,
    MatIconModule,
    MatTooltipModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    TextInputComponent,
    TextAreaComponent,
    SlideToggleInputComponent,
    SelectInputComponent,
    MaporiumAccordionItemComponent,
    TranslatePipe
  ],
  standalone: true
})
export class CreateFilterDialogComponent implements OnInit {
  myForm: FormGroup;
  selectedConditionIndex: number | null = null;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const targetElement = event.target as HTMLElement;

    // Check if clicked outside of any condition items
    if (!targetElement.closest('.condition-item')) {
      this.selectedConditionIndex = null;
    }
  }

  subjectOptions: { key: FilterSubject; value: string }[];
  verbOptions: { key: FilterVerb; value: string }[];
  matchTypeOptions: { key: FilterMatchType; value: string }[];
  conditionTypeOptions: { key: FilterConditionType; value: string }[];
  actionTypeOptions: { key: FilterActionType; value: string }[];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: FilterDto | null,
    private matDialogRef: MatDialogRef<CreateFilterDialogComponent>,
    private filterService: FilterService,
    private mapService: MapService,
    private formBuilder: FormBuilder,
    private translate: TranslateService
  ) {
    this.subjectOptions = [
      { key: FilterSubject.ELEMENT, value: this.translate.instant('FILTER_DIALOG.SUBJECT.ELEMENT') },
      { key: FilterSubject.NAME, value: this.translate.instant('FILTER_DIALOG.SUBJECT.NAME') },
      { key: FilterSubject.DESCRIPTION, value: this.translate.instant('FILTER_DIALOG.SUBJECT.DESCRIPTION') },
      { key: FilterSubject.PROPERTY, value: this.translate.instant('FILTER_DIALOG.SUBJECT.PROPERTY') },
      { key: FilterSubject.COMMENT, value: this.translate.instant('FILTER_DIALOG.SUBJECT.COMMENT') }
    ];

    this.verbOptions = [
      { key: FilterVerb.CONTAINS, value: this.translate.instant('FILTER_DIALOG.VERB.CONTAINS') },
      { key: FilterVerb.STARTS_WITH, value: this.translate.instant('FILTER_DIALOG.VERB.STARTS_WITH') },
      { key: FilterVerb.ENDS_WITH, value: this.translate.instant('FILTER_DIALOG.VERB.ENDS_WITH') },
      { key: FilterVerb.EQUALS, value: this.translate.instant('FILTER_DIALOG.VERB.EQUALS') },
      { key: FilterVerb.DOES_NOT_CONTAIN, value: this.translate.instant('FILTER_DIALOG.VERB.DOES_NOT_CONTAIN') },
      { key: FilterVerb.DOES_NOT_START_WITH, value: this.translate.instant('FILTER_DIALOG.VERB.DOES_NOT_START_WITH') },
      { key: FilterVerb.DOES_NOT_END_WITH, value: this.translate.instant('FILTER_DIALOG.VERB.DOES_NOT_END_WITH') },
      { key: FilterVerb.DOES_NOT_EQUAL, value: this.translate.instant('FILTER_DIALOG.VERB.DOES_NOT_EQUAL') }
    ];

    this.matchTypeOptions = [
      { key: FilterMatchType.MATCH, value: this.translate.instant('FILTER_DIALOG.MATCH_TYPE.MATCH') },
      { key: FilterMatchType.DO_NOT_MATCH, value: this.translate.instant('FILTER_DIALOG.MATCH_TYPE.DO_NOT_MATCH') }
    ];

    this.conditionTypeOptions = [
      { key: FilterConditionType.ALL_CONDITIONS, value: this.translate.instant('FILTER_DIALOG.CONDITION_TYPE.ALL_CONDITIONS') },
      { key: FilterConditionType.ANY_CONDITION, value: this.translate.instant('FILTER_DIALOG.CONDITION_TYPE.ANY_CONDITION') }
    ];

    this.actionTypeOptions = [
      { key: FilterActionType.ARE_DIMMED, value: this.translate.instant('FILTER_DIALOG.ACTION_TYPE.ARE_DIMMED') },
      { key: FilterActionType.ARE_HIDDEN, value: this.translate.instant('FILTER_DIALOG.ACTION_TYPE.ARE_HIDDEN') }
    ];

    this.myForm = this.formBuilder.group({
      name: ['', Validators.required],
      description: [''],
      excludeFromReadOnly: [false],
      isGlobal: [false],
      conditions: this.formBuilder.array([]),
      action: this.formBuilder.group({
        matchType: [FilterMatchType.DO_NOT_MATCH, Validators.required],
        conditionType: [FilterConditionType.ALL_CONDITIONS, Validators.required],
        actionType: [FilterActionType.ARE_DIMMED, Validators.required]
      })
    });

    // Initialize with default condition
    if (!this.data) {
      this.addCondition();
    }
  }

  get conditionsArray() {
    return this.myForm?.get('conditions') as FormArray;
  }

  ngOnInit(): void {
    if (this.data) {
      this.myForm.patchValue({
        name: this.data.name,
        description: this.data.description,
        excludeFromReadOnly: this.data.excludeFromReadOnly,
        isGlobal: this.data.isGlobal,
        action: this.data.action
      });

      // Populate conditions
      if (this.data.conditions && this.data.conditions.length > 0) {
        this.data.conditions.forEach(condition => {
          this.conditionsArray.push(this.createConditionFormGroup(condition));
        });
      } else {
        // Add default condition if none exist
        this.addCondition();
      }
    }
  }

  createConditionFormGroup(condition?: FilterConditionDto): FormGroup {
    return this.formBuilder.group({
      subject: [condition?.subject || FilterSubject.ELEMENT, Validators.required],
      verb: [condition?.verb || FilterVerb.CONTAINS, Validators.required],
      object: [condition?.object || '', Validators.required],
      propertyName: [condition?.propertyName || '']
    });
  }

  addCondition() {
    this.conditionsArray.push(this.createConditionFormGroup());
  }

  removeCondition(index: number) {
    if (this.conditionsArray.length > 1 && index !== null) {
      this.conditionsArray.removeAt(index);
      this.selectedConditionIndex = null;
    }
  }

  selectCondition(index: number) {
    this.selectedConditionIndex = index;
  }

  dropCondition(event: CdkDragDrop<FormGroup[]>) {
    const conditionsArray = this.conditionsArray;
    const draggedControl = conditionsArray.at(event.previousIndex);

    // Remove the dragged control from its old position
    conditionsArray.removeAt(event.previousIndex);

    // Insert it at the new position
    conditionsArray.insert(event.currentIndex, draggedControl);

    // Update selected index if needed
    if (this.selectedConditionIndex === event.previousIndex) {
      this.selectedConditionIndex = event.currentIndex;
    } else if (this.selectedConditionIndex !== null) {
      if (event.previousIndex < this.selectedConditionIndex && event.currentIndex >= this.selectedConditionIndex) {
        this.selectedConditionIndex--;
      } else if (event.previousIndex > this.selectedConditionIndex && event.currentIndex <= this.selectedConditionIndex) {
        this.selectedConditionIndex++;
      }
    }
  }

  create() {
    if (this.myForm?.valid) {
      const currentMap = this.mapService.getCurrentSelectedMapFromStore();
      if (!currentMap?.id) {
        console.error('No current map selected');
        return;
      }

      const payload: Partial<FilterDto> = {
        name: this.myForm.value.name,
        description: this.myForm.value.description,
        excludeFromReadOnly: this.myForm.value.excludeFromReadOnly,
        isGlobal: this.myForm.value.isGlobal,
        conditions: this.myForm.value.conditions,
        action: this.myForm.value.action,
        mapId: currentMap.id
      };

      this.filterService.save(payload).subscribe((filter) => {
        this.matDialogRef.close(filter);
      });
    }
  }

  edit() {
    if (this.myForm?.valid && this.data?.id) {
      const payload: Partial<FilterDto> = {
        name: this.myForm.value.name,
        description: this.myForm.value.description,
        excludeFromReadOnly: this.myForm.value.excludeFromReadOnly,
        isGlobal: this.myForm.value.isGlobal,
        conditions: this.myForm.value.conditions,
        action: this.myForm.value.action
      };

      this.filterService.update(this.data.id, payload).subscribe((filter) => {
        this.matDialogRef.close(filter);
      });
    }
  }
}
